import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://g1sport.fr/api';
// console.log('API_URL used:', API_URL);

const api = axios.create({
  baseURL: API_URL,
});

// Public browsing never reuses authentication or refresh interceptors.
export const publicApi = axios.create({ baseURL: API_URL });

// This standard header does not change authentication or API field values.
for (const client of [api, publicApi]) {
  client.interceptors.request.use(config => {
    try {
      const language = localStorage.getItem('g1sport.language') || 'fr';
      config.headers['Accept-Language'] = ['fr', 'en', 'es', 'pt', 'it', 'de', 'zh', 'ar'].includes(language) ? language : 'fr';
    } catch { config.headers['Accept-Language'] = 'fr'; }
    return config;
  });
}


// NB : l'app native est identifiee cote backend via le User-Agent (marqueur
// « G1SportNative » ajoute par Capacitor). On n'envoie PAS d'en-tete custom
// pour eviter toute preflight CORS (qui faisait echouer des requetes).
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // console.log('Adding token to request:', config.url);
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({resolve, reject});
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        processQueue(error, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        isRefreshing = false;
        
        const protectedPaths = ['/coach', '/sportif', '/messages'];
        const currentPath = window.location.pathname;
        
        // Explicitly allow root path and other public paths to avoid redirect loops or unwanted redirects
        if (currentPath === '/' || currentPath === '/login' || currentPath === '/register') {
             return Promise.reject(error);
        }

        const isProtected = protectedPaths.some(path => currentPath.startsWith(path));
        
        if (isProtected) {
            window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      try {
        const response = await axios.post(`${API_URL}/users/token/refresh/`, {
          refresh: refreshToken,
        });
        
        const { access } = response.data;
        
        localStorage.setItem('access_token', access);
        api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        originalRequest.headers.Authorization = `Bearer ${access}`;
        
        processQueue(null, access);
        isRefreshing = false;
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        
        const currentPath = window.location.pathname;
        if (currentPath === '/' || currentPath === '/login' || currentPath === '/register') {
             return Promise.reject(refreshError);
        }

        const protectedPaths = ['/coach', '/sportif', '/messages'];
        const isProtected = protectedPaths.some(path => currentPath.startsWith(path));
        
        if (isProtected) {
            window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
